import React, { useCallback } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DivisionMileStone } from '../../../../../../api/divisionMileStones/types';
import { BaseChip } from './BaseChip';
import { styles } from './MilestoneAccordion.styles';

interface TaskItemProps {
     task: DivisionMileStone;
     getTaskCheckboxState: (taskId: string) => boolean;
     onTaskCheckboxChange: (taskId: string, checked: boolean) => void;
     getTaskChipType: (task: DivisionMileStone) => {
          label: string;
          color: 'default' | 'info' | 'success';
          customColor?: string
     };
     dragHandleProps?: any;
     isDragging?: boolean;
     divisionSettingsReordering?: boolean;
}

export const TaskItem: React.FC<TaskItemProps> = React.memo(({
     task,
     getTaskCheckboxState,
     onTaskCheckboxChange,
     getTaskChipType,
     dragHandleProps,
     isDragging,
     divisionSettingsReordering,
}) => {
     const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation();
          onTaskCheckboxChange(task.id, e.target.checked);
     }, [task.id, onTaskCheckboxChange]);

     const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
          e.stopPropagation();
     }, []);

     const currentCheckboxState = getTaskCheckboxState(task.id);
     const isIndeterminate = !task.status && currentCheckboxState === false;

     const isDragDisabled = React.useMemo(() => {
          const restrictedTaskNames = [
               'Track and confirm 5-day Application date/Proof of funds',
               'Loan Docs received'
          ].map(name => name.toLowerCase().trim());
          
          const currentTaskName = (task.taskName || '').toLowerCase().trim();
          return task.automated || restrictedTaskNames.includes(currentTaskName);
     }, [task.automated, task.taskName]);

     return (
          <Box
               key={task.id}
               sx={{
                    ...styles.taskItem,
                    // Add visual feedback when dragging
                    backgroundColor: isDragging ? '#e3f2fd' : 'transparent',
                    boxShadow: isDragging ? '0 4px 8px rgba(0, 0, 0, 0.15)' : 'none',
                    borderRadius: isDragging ? '4px' : 0,
                    opacity: isDragging ? 0.9 : 1,
               }}
          >
               <Checkbox
                    checked={currentCheckboxState}
                    indeterminate={isIndeterminate}
                    disabled={task.automated}
                    onChange={handleCheckboxChange}
                    onClick={handleCheckboxClick}
                    sx={styles.taskCheckbox}
                    aria-label={`${task.automated ? 'Automated' : 'Select'} task: ${task.taskName}`}
               />
               <Box sx={styles.taskContent}>
                    <Typography sx={styles.taskName}>
                         {task.taskName}
                    </Typography>
                    <BaseChip
                         task={task}
                         getTaskChipType={getTaskChipType}
                         getTaskCheckboxState={getTaskCheckboxState}
                    />
               </Box>
               {/* Drag handle for task reordering - hidden for automated and restricted tasks */}
               {dragHandleProps && divisionSettingsReordering && !isDragDisabled && (
                    <Box
                         {...dragHandleProps}
                         sx={{
                              cursor: 'grab',
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              padding: 0.5,
                              borderRadius: 1,
                              marginLeft: 'auto',
                              '&:hover': {
                                   backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                              '&:active': {
                                   cursor: 'grabbing',
                              },
                         }}
                    >
                         <DragIndicatorIcon fontSize="small" />
                    </Box>
               )}
          </Box>
     );
});